/**
 * AI Slash Commands Registry
 *
 * Available commands with auto-complete filtering and smart routing to prompts.
 * Type "/" in the admin AI chat to see available commands.
 */

export interface SlashCommand {
  command: string      // e.g. "stats"
  aliases?: string[]   // e.g. ["dashboard", "overview"]
  description: string
  prompt: string       // The full prompt to send to the LLM
  icon: string         // lucide icon name
  category: 'analytics' | 'products' | 'catalog' | 'admin' | 'code'
}

export const SLASH_COMMANDS: SlashCommand[] = [
  // ── Analytics ──────────────────────────────────────────────────────────────
  {
    command: 'stats',
    aliases: ['dashboard', 'overview'],
    description: 'Get a complete dashboard overview: revenue, products, orders, buyers',
    prompt: 'Give me a complete dashboard overview: total revenue, products count, orders this month, registered buyers, and recent orders.',
    icon: 'BarChart2',
    category: 'analytics',
  },
  {
    command: 'analytics',
    aliases: ['trends', 'performance'],
    description: 'Show analytics for the last 30 days with trends and top sellers',
    prompt: 'Show me analytics for the last 30 days: revenue, confirmed orders, top selling items, and order breakdown by status.',
    icon: 'TrendingUp',
    category: 'analytics',
  },
  {
    command: 'analytics-week',
    aliases: ['week', 'last-7'],
    description: 'Show analytics for the last 7 days',
    prompt: 'Show me analytics for the last 7 days.',
    icon: 'TrendingUp',
    category: 'analytics',
  },
  {
    command: 'treasury',
    aliases: ['accounting', 'revenue'],
    description: 'Show treasury overview: revenue by chain and monthly P&L',
    prompt: 'Show me treasury overview: total revenue, confirmed and pending orders, revenue breakdown by payment method and chain, and monthly revenue trend.',
    icon: 'Wallet',
    category: 'analytics',
  },
  {
    command: 'orders',
    aliases: ['recent', 'orders-all'],
    description: 'Show the last 20 orders with buyer and status',
    prompt: 'Show me the last 20 orders including buyer name, item, amount, and status.',
    icon: 'ShoppingCart',
    category: 'analytics',
  },
  {
    command: 'orders-pending',
    aliases: ['pending-orders'],
    description: 'Show all pending or confirming orders',
    prompt: 'List all orders with pending or confirming status. Show buyer, item, amount, and time since creation.',
    icon: 'Clock',
    category: 'analytics',
  },

  // ── Product Catalog ────────────────────────────────────────────────────────
  {
    command: 'products',
    aliases: ['list-products', 'product-list'],
    description: 'List all products including drafts with status and price',
    prompt: 'List all products including drafts. Show status (published or draft), price, and category. Highlight any that could be published.',
    icon: 'Package',
    category: 'products',
  },
  {
    command: 'contracts',
    aliases: ['list-contracts', 'smart-contracts'],
    description: 'List all smart contracts including drafts',
    prompt: 'List all smart contracts including drafts. Show status, type, base price, and effective price (with certification if applicable).',
    icon: 'Code2',
    category: 'products',
  },
  {
    command: 'bundles',
    aliases: ['list-bundles'],
    description: 'List all bundles',
    prompt: 'List all bundles. Show which product and contract are included, status, and price.',
    icon: 'Layers',
    category: 'products',
  },
  {
    command: 'drive',
    aliases: ['google-drive', 'files'],
    description: 'List all Google Drive files and show which are linked to catalog items',
    prompt: 'List all files in Google Drive. Show which files are already linked to products, contracts, or bundles.',
    icon: 'HardDrive',
    category: 'catalog',
  },

  // ── Create Operations ──────────────────────────────────────────────────────
  {
    command: 'create-product',
    aliases: ['new-product', '+product'],
    description: 'Create a new product',
    prompt: 'I want to create a new product. Ask me for: name, tagline (short), full description, category, price in USD, and optionally a Google Drive file ID.',
    icon: 'Plus',
    category: 'products',
  },
  {
    command: 'create-contract',
    aliases: ['new-contract', '+contract'],
    description: 'Create a new smart contract listing',
    prompt: 'I want to create a new smart contract. Ask me for: name, description, contract type, base price in USD, solidity version, networks tested, and optionally a Drive file ID.',
    icon: 'Plus',
    category: 'products',
  },
  {
    command: 'create-bundle',
    aliases: ['new-bundle', '+bundle'],
    description: 'Create a new bundle from products and contracts',
    prompt: 'Help me create a new bundle. First list all products and contracts, then ask me which product and contract to pair, what price, and the savings percentage.',
    icon: 'Plus',
    category: 'products',
  },
  {
    command: 'create-article',
    aliases: ['new-article', '+article'],
    description: 'Create a new education article',
    prompt: 'I want to create a new education article. Ask me for: title, excerpt (short summary), category, and the article content in Markdown.',
    icon: 'Plus',
    category: 'admin',
  },
  {
    command: 'create-pricing',
    aliases: ['new-pricing', '+pricing'],
    description: 'Create a new pricing tier',
    prompt: 'I want to create a new pricing tier. Ask me for: title, description, category, min and max price, features list, and icon.',
    icon: 'Plus',
    category: 'admin',
  },

  // ── Publish Operations ────────────────────────────────────────────────────
  {
    command: 'publish-all',
    aliases: ['publish', 'review-drafts'],
    description: 'Review and publish draft items',
    prompt: 'List all draft products, contracts, and bundles. Ask me which ones I want to publish.',
    icon: 'Send',
    category: 'products',
  },
  {
    command: 'unpublish',
    aliases: ['hide-items', 'unpublish-all'],
    description: 'Unpublish published items',
    prompt: 'Show me all published products, contracts, and bundles. Ask me which ones I want to unpublish (hide from catalog).',
    icon: 'Eye',
    category: 'products',
  },

  // ── Order & Refund Management ─────────────────────────────────────────────
  {
    command: 'refund',
    aliases: ['process-refund', 'issue-refund'],
    description: 'Review and refund an order',
    prompt: 'Show recent confirmed orders. Ask me which order to refund and the reason.',
    icon: 'RotateCcw',
    category: 'admin',
  },

  // ── Content Management ────────────────────────────────────────────────────
  {
    command: 'articles',
    aliases: ['education', 'learn'],
    description: 'List all education articles',
    prompt: 'List all education articles including drafts. Show title, category, status, and reading time.',
    icon: 'BookOpen',
    category: 'admin',
  },
  {
    command: 'pricing',
    aliases: ['pricing-tiers', 'plans'],
    description: 'List all pricing tiers',
    prompt: 'List all pricing tiers (published and drafts). Show title, category, price range, and status.',
    icon: 'DollarSign',
    category: 'admin',
  },
  {
    command: 'carousel',
    aliases: ['hero', 'banner'],
    description: 'List all carousel images',
    prompt: 'List all carousel images ordered by display order. Show title, URL, and whether each is active.',
    icon: 'Image',
    category: 'admin',
  },

  // ── Admin & Settings ───────────────────────────────────────────────────────
  {
    command: 'settings',
    aliases: ['config', 'configuration'],
    description: 'Show current platform settings',
    prompt: 'Show all current platform settings and their values (masking secrets).',
    icon: 'Settings',
    category: 'admin',
  },
  {
    command: 'keys',
    aliases: ['api-keys', 'agent-keys'],
    description: 'List all agent API keys with usage stats',
    prompt: 'List all agent API keys. Show name, prefix, when created, last used, and usage metrics (successful calls and failed attempts in last hour).',
    icon: 'Key',
    category: 'admin',
  },
  {
    command: 'audit',
    aliases: ['audit-log', 'security-log'],
    description: 'Show API key audit log with suspicious activity',
    prompt: 'Show the API key audit log with recent activity. Highlight any suspicious patterns like failed authentication attempts.',
    icon: 'AlertCircle',
    category: 'admin',
  },

  // ── Help ───────────────────────────────────────────────────────────────────
  {
    command: 'help',
    aliases: ['commands', 'usage'],
    description: 'List all available slash commands',
    prompt: 'List all available / commands I can use in this AI assistant, grouped by category, with descriptions.',
    icon: 'HelpCircle',
    category: 'admin',
  },

  // ── Code Editing & Git (dev server only) ────────────────────────────────────
  {
    command: 'read',
    aliases: ['open-file'],
    description: 'Read and display a file',
    prompt: 'Read and show me the contents of a file. Ask me which file to read.',
    icon: 'FileText',
    category: 'code',
  },
  {
    command: 'edit',
    aliases: ['edit-file'],
    description: 'Edit a file with find-and-replace',
    prompt: 'I want to edit a file. Ask me which file, read it, then help me modify it using find-and-replace.',
    icon: 'Edit2',
    category: 'code',
  },
  {
    command: 'search',
    aliases: ['grep', 'find-code'],
    description: 'Search the codebase',
    prompt: 'Search the codebase for a pattern. Ask me what to search for and in which files.',
    icon: 'Search',
    category: 'code',
  },
  {
    command: 'git-diff',
    aliases: ['diff'],
    description: 'Show pending git changes',
    prompt: 'Show me the current git diff of all pending changes.',
    icon: 'GitBranch',
    category: 'code',
  },
  {
    command: 'git-status',
    aliases: ['status'],
    description: 'Show git repository status',
    prompt: 'Show me the current git status with all modified and untracked files.',
    icon: 'GitBranch',
    category: 'code',
  },
  {
    command: 'commit',
    aliases: ['git-commit'],
    description: 'Commit pending changes',
    prompt: 'Show me git status, then ask me for a commit message and stage all pending changes to commit.',
    icon: 'GitCommit',
    category: 'code',
  },
  {
    command: 'git-history',
    aliases: ['log', 'git-log'],
    description: 'Show recent commits',
    prompt: 'Show me the last 15 git commits with messages and authors.',
    icon: 'History',
    category: 'code',
  },
  {
    command: 'build',
    aliases: ['type-check', 'tsc'],
    description: 'Run build and type checks',
    prompt: 'Run the TypeScript build check and report any errors or warnings.',
    icon: 'Terminal',
    category: 'code',
  },
]

/**
 * Search for commands by filter (command name or description)
 */
export function searchCommands(filter: string): SlashCommand[] {
  const lowerFilter = filter.toLowerCase().trim()
  if (!lowerFilter) return SLASH_COMMANDS

  return SLASH_COMMANDS.filter((cmd) => {
    const matchCommand = cmd.command.toLowerCase().includes(lowerFilter)
    const matchDescription = cmd.description.toLowerCase().includes(lowerFilter)
    const matchAlias = cmd.aliases?.some((a) => a.toLowerCase().includes(lowerFilter)) ?? false
    return matchCommand || matchDescription || matchAlias
  })
}
